import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { MascotSmall } from './Mascot';

const navItems = [
  { label: "Fonctionnalités", href: "#solution" },
  { label: "Comment ça marche", href: "#how-it-works" },
  { label: "Technologie", href: "#technology" }
];

export const Header = ({ onCtaClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? isDark 
              ? 'bg-black/80 backdrop-blur-xl backdrop-saturate-150' 
              : 'bg-white/80 backdrop-blur-xl backdrop-saturate-150'
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        data-testid="header"
      >
        <nav className="max-w-[980px] mx-auto px-6 h-12 flex items-center justify-between">
          {/* Logo */}
          <button 
            onClick={() => scrollToSection('#hero')}
            className="flex items-center gap-2 focus:outline-none"
            data-testid="header-logo"
          >
            <MascotSmall className="w-7 h-7" />
            <span className={`text-[17px] font-semibold tracking-[-0.02em] ${
              isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
            }`}>
              <span className="text-[#81d8d0]">Skip</span>ya
            </span>
          </button>

          {/* Desktop nav - Apple style */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(item.href)}
                className={`text-[12px] font-normal tracking-[-0.01em] transition-colors ${
                  isDark 
                    ? 'text-[#f5f5f7]/80 hover:text-[#f5f5f7]' 
                    : 'text-[#1d1d1f]/80 hover:text-[#1d1d1f]'
                }`}
                data-testid={`nav-item-${index}`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Right side - minimal icons */}
          <div className="flex items-center gap-4">
            {/* Theme toggle - just icon, no background */}
            <button
              onClick={toggleTheme}
              className={`p-1 transition-colors ${
                isDark ? 'text-[#f5f5f7]/60 hover:text-[#f5f5f7]' : 'text-[#1d1d1f]/60 hover:text-[#1d1d1f]'
              }`}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
            </button>

            {/* Login link - Apple text style */}
            <Link 
              to="/login" 
              className={`hidden sm:block text-[12px] font-normal tracking-[-0.01em] transition-colors ${
                isDark 
                  ? 'text-[#f5f5f7]/80 hover:text-[#f5f5f7]' 
                  : 'text-[#1d1d1f]/80 hover:text-[#1d1d1f]'
              }`}
              data-testid="header-login"
            >
              Connexion
            </Link>

            {/* CTA - Apple minimal */}
            <button
              onClick={onCtaClick}
              className="hidden sm:flex items-center text-[12px] font-normal text-[#81d8d0] hover:text-[#6bc4bc] transition-colors"
              data-testid="header-cta"
            >
              Commencer
            </button>

            {/* Mobile menu - thin icon */}
            <button
              className={`md:hidden p-1 ${isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
            </button>
          </div>
        </nav>

        {/* Subtle border when scrolled */}
        {isScrolled && (
          <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-black/5'}`} />
        )}
      </motion.header>

      {/* Mobile menu - Apple style */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className={`fixed inset-x-0 top-12 z-40 md:hidden ${
              isDark ? 'bg-black/95 backdrop-blur-xl' : 'bg-white/95 backdrop-blur-xl'
            }`}
          >
            <div className="max-w-[980px] mx-auto px-6 py-6 space-y-6">
              {navItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(item.href)}
                  className={`block text-[17px] font-normal ${
                    isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-black/5'}`} />
              <Link 
                to="/login"
                className={`block text-[17px] font-normal ${
                  isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Connexion
              </Link>
              <button
                onClick={() => { onCtaClick?.(); setIsMobileMenuOpen(false); }}
                className="text-[17px] font-normal text-[#81d8d0]"
              >
                Commencer
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
